import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './Pricing.css';

function priceLabel(cents) {
  if (cents === null || cents === undefined) return 'Custom';
  if (cents === 0) return '$0';
  return `$${(cents / 100).toFixed(0)}`;
}

function creditsLabel(credits) {
  if (credits === null || credits === undefined) return 'Unlimited';
  return credits.toLocaleString();
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // plan id currently being checked out
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api.get('/api/billing/plans')
      .then((res) => { if (!cancelled) { setPlans(res.plans || []); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const upgrade = async (planId) => {
    setError('');
    if (!user) {
      // Send to signup, remember the chosen plan via querystring.
      navigate(`/auth?plan=${encodeURIComponent(planId)}`);
      return;
    }
    if (planId === 'free') {
      // Free is the default tier — no checkout to make. Authed users land
      // on the dashboard; if they were on a paid plan they can cancel via
      // Settings → Manage billing.
      navigate('/portal');
      return;
    }
    if (planId === 'enterprise') {
      window.location.href = 'mailto:sales@akakai.com?subject=Enterprise%20inquiry';
      return;
    }
    try {
      setBusy(planId);
      const { url } = await api.post('/api/billing/checkout', { plan_id: planId });
      window.location.href = url;
    } catch (e) {
      setError(e.message);
      setBusy(null);
    }
  };

  // Re-order so the public catalog reads: tiered (free → max), then a
  // separate row for pay-as-you-go and enterprise.
  const tiered = plans.filter(p => ['free', 'basic', 'plus', 'pro', 'max'].includes(p.id));
  const usagePlan = plans.find(p => p.id === 'usage');
  const enterprise = plans.find(p => p.id === 'enterprise');
  const cancelled = params.get('billing') === 'cancel';

  return (
    <div className="pricing-page">
      <nav className="pricing-nav">
        <div className="nav-left">
          <button className="nav-wordmark" onClick={() => navigate('/')}>AEGENT</button>
        </div>
        <div className="nav-right">
          {user ? (
            <button className="nav-link" onClick={() => navigate('/settings')}>Settings</button>
          ) : (
            <button className="nav-link" onClick={() => navigate('/auth')}>Sign In</button>
          )}
          <button className="nav-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? '○' : '●'}
          </button>
        </div>
      </nav>

      <main className="pricing-main">
        <motion.header
          className="pricing-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="t-caption pricing-eyebrow">Pricing</p>
          <h1 className="t-display pricing-title">Pay for<br/>what runs.</h1>
        </motion.header>

        {cancelled && (
          <div className="pricing-banner">
            Checkout cancelled — your plan is unchanged.
          </div>
        )}
        {error && (
          <div className="pricing-banner pricing-banner-error">{error}</div>
        )}

        {loading ? (
          <div className="pricing-loading"><div className="loading-spinner" /></div>
        ) : (
          <>
            <section className="pricing-grid">
              {tiered.map((plan, i) => (
                <motion.div
                  key={plan.id}
                  className={`pricing-card${plan.popular ? ' pricing-card-popular' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  {plan.popular && <span className="pricing-popular">Most popular</span>}
                  <div className="pricing-card-head">
                    <h2 className="t-h2 pricing-card-name">{plan.name}</h2>
                    <p className="pricing-card-tagline">{plan.tagline}</p>
                  </div>
                  <div className="pricing-card-price">
                    <span className="pricing-amount">{priceLabel(plan.price_cents)}</span>
                    {plan.price_cents > 0 && <span className="pricing-period">/ month</span>}
                  </div>
                  <div className="pricing-cap">
                    <span className="pricing-cap-num">{creditsLabel(plan.monthly_credits)}</span>
                    <span className="pricing-cap-label">credits / month</span>
                  </div>
                  {plan.overage_supported && plan.overage_rate_cents != null && (
                    <div className="pricing-overage">
                      <div className="pricing-overage-head">
                        <span className="pricing-overage-label">Overage</span>
                        <span className="pricing-overage-tag">Optional</span>
                      </div>
                      <div className="pricing-overage-rate">
                        ${(plan.overage_rate_cents / 100).toFixed(2)}<span className="pricing-overage-suffix"> / credit</span>
                      </div>
                      {plan.burst_mult && plan.monthly_credits && (
                        <span className="pricing-overage-sub">
                          Up to {(plan.monthly_credits * plan.burst_mult).toLocaleString()} credits / mo
                        </span>
                      )}
                    </div>
                  )}
                  <ul className="pricing-features">
                    {(plan.features || []).map(f => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <button
                    className={`btn ${plan.popular ? 'btn-primary' : ''} pricing-cta`}
                    disabled={busy === plan.id || (!plan.billable && plan.id !== 'free')}
                    onClick={() => upgrade(plan.id)}
                  >
                    {busy === plan.id ? '...' : plan.id === 'free' ? 'Start free' : `Choose ${plan.name}`}
                  </button>
                </motion.div>
              ))}
            </section>

            <section className="pricing-secondary">
              {usagePlan && (
                <motion.div
                  className="pricing-card pricing-card-wide"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <div className="pricing-wide-left">
                    <p className="t-caption">Pay as you go</p>
                    <h2 className="t-h1 pricing-wide-title">$0.05<span className="pricing-wide-suffix">/credit</span></h2>
                    <p className="pricing-wide-sub">{usagePlan.tagline}</p>
                    <ul className="pricing-features pricing-features-inline">
                      {(usagePlan.features || []).map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                  <div className="pricing-wide-right">
                    <button
                      className="btn btn-primary btn-lg"
                      disabled={busy === 'usage' || !usagePlan.billable}
                      onClick={() => upgrade('usage')}
                    >
                      {busy === 'usage' ? '...' : 'Start metered'}
                    </button>
                    <p className="pricing-wide-note">Billed monthly. Cancel anytime.</p>
                  </div>
                </motion.div>
              )}

              {enterprise && (
                <motion.div
                  className="pricing-card pricing-card-wide pricing-card-enterprise"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                >
                  <div className="pricing-wide-left">
                    <p className="t-caption">Enterprise</p>
                    <h2 className="t-h1 pricing-wide-title">Custom</h2>
                    <p className="pricing-wide-sub">{enterprise.tagline}</p>
                    <ul className="pricing-features pricing-features-inline">
                      {(enterprise.features || []).map(f => <li key={f}>{f}</li>)}
                    </ul>
                  </div>
                  <div className="pricing-wide-right">
                    <button className="btn btn-lg" onClick={() => upgrade('enterprise')}>
                      Contact sales
                    </button>
                    <p className="pricing-wide-note">SSO, custom SLA, dedicated support.</p>
                  </div>
                </motion.div>
              )}
            </section>

            <section className="pricing-faq">
              <h3 className="t-h3 pricing-faq-title">How it works</h3>
              <div className="pricing-faq-grid">
                <div className="pricing-faq-item">
                  <h4>What happens when I run out of credits?</h4>
                  <p>By default, your agents pause until your credits reset at the start of your next billing month. Turn on overage in Settings to keep running, charged per credit.</p>
                </div>
                <div className="pricing-faq-item">
                  <h4>Do unused credits roll over?</h4>
                  <p>No — each plan includes a fresh monthly grant of credits that resets every billing month.</p>
                </div>
                <div className="pricing-faq-item">
                  <h4>Can I switch plans?</h4>
                  <p>Yes, anytime. Upgrades are prorated; downgrades take effect at the end of your billing period.</p>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <footer className="pricing-footer">
        <span>© Aegent</span>
        <div>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/docs">Docs</a>
        </div>
      </footer>
    </div>
  );
}
